import { useState, useEffect } from 'react';
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import * as theme from '../../theme';
import Button from "@mui/material/Button";
import { formatPhone } from '../../functions/InputHelpers';
import ErrorDisplay from '../../components/ErrorDisplay';

export default function EditPractitioner() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    generalNotes: '',
    supplementLines: ''
  });
  const [originalData, setOriginalData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    generalNotes: '',
    supplementLines: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    if (id != null) {
      getPractitioner();
    }
  }, [id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (e) => {
    const input = e.target.value;
    if (/^[0-9-]*$/.test(input)) {
      const formatted = formatPhone(input);
      setFormData({ ...formData, [e.target.name]: formatted });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const practitionerData = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        businessName: formData.businessName.trim() || null,
        dateModified: serverTimestamp(),
      };

      const practitionerRef = doc(db, "practitioners", id);
      await updateDoc(practitionerRef, practitionerData);

      const fieldsToCheck = ['firstName', 'lastName', 'businessName'];
      const shouldUpdatePractitioner = fieldsToCheck.some(field => 
        formData[field] !== originalData[field]
      );

      if (shouldUpdatePractitioner) {
        const managementRef = doc(db, "app", 'management');
        
        const managementSnap = await getDoc(managementRef);
        if (managementSnap.exists()) {
          const managementData = managementSnap.data();
          const updatedPractitioners = managementData.practitioners.map(practitioner => {
            if (practitioner.id === id) {
              return {
                ...practitioner,
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                businessName: formData.businessName.trim() || null
              };
            }
            return practitioner;
          });
  
          await updateDoc(managementRef, {
            practitioners: updatedPractitioners
          });
        }

        const usersRef = doc(db, "users", id);
        await updateDoc(usersRef, { 
          firstName: formData.firstName.trim(), 
          lastName: formData.lastName.trim()
        });
      }

      navigateBack();
    } catch (err) {
      setError("Failed to edit practitioner: " + err.message);
    }

    setLoading(false);
  };

  function navigateBack() {
    if (currentUser.role === 'practitioner') {
      navigate(`/dashboard`);
    } else {
      navigate(`/practitioners/${id}`);
    }
  }

  async function getPractitioner() {
    try {
      const practitionerRef = doc(db, "practitioners", id);
      const docSnap = await getDoc(practitionerRef);
      const practitionerData = docSnap.data();
      setFormData({
        ...practitionerData,
        businessName: practitionerData.businessName || ''
      });
      setOriginalData({
        ...practitionerData,
        businessName: practitionerData.businessName || ''
      });
    } catch (err) {
      console.log("Failed get practitioner: " + err.message);
    }
  }

  return (
    <div style={theme.Container.basic}>
      <div style={theme.Heading}>Edit Practitioner</div>
      <div style={theme.SubHeadingText}>
        {currentUser.role === 'practitioner'
          ? `Fill out the form below to edit your information.`
          : `Fill out the form below to edit the practitioner's information.`}
      </div>
      <div style={{ marginTop: 15, marginBottom: 30 }}>
        <div style={{ marginTop: 0, marginBottom: error ? 30 : 10 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </div>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 4 }}
        >
          <TextField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            variant="filled"
            required
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            variant="filled"
            required
          />
          <TextField
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            variant="filled"
            disabled
          />
          <TextField
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handlePhoneChange}
            slotProps={{
              htmlInput: {
                inputMode: 'numeric',
                maxLength: 12,
              }
            }}
            variant="filled"
            required
          />
          <TextField
            label="Business Name (Optional)"
            name="businessName"
            value={formData.businessName}
            onChange={handleChange}
            variant="filled"
          />
          <TextField
            label="General Notes"
            name="generalNotes"
            value={formData.generalNotes}
            onChange={handleChange}
            variant="filled"
            multiline
            rows={4}
          />
          <TextField
            label="Supplement Lines Added"
            name="supplementLines"
            value={formData.supplementLines}
            onChange={handleChange}
            variant="filled"
            multiline
            rows={4}
          />
          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigateBack()}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 3 }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
      </div>
    </div>
  );
}